import React, { useState, useEffect } from 'react';
import trainerService from '../../services/trainerService';
import authService from '../../services/auth';

/**
 * Trainer Page component
 * Provides UI for generating and managing Q&A pairs for the chatbot
 */
const TrainerPage = () => {
  // User state
  const [userId, setUserId] = useState(null);
  
  // Document input state
  const [documentText, setDocumentText] = useState('');
  const [numQuestions, setNumQuestions] = useState(5);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Content source state
  const [contentSource, setContentSource] = useState('provided_text'); // 'provided_text' or 'knowledge_base'
  const [knowledgeBaseQuery, setKnowledgeBaseQuery] = useState('');
  
  // Generated Q&A pairs state
  const [qaPairs, setQaPairs] = useState([]);
  const [selectedQaPairs, setSelectedQaPairs] = useState([]);
  
  // Validation state
  const [validationResults, setValidationResults] = useState({});
  const [isValidating, setIsValidating] = useState(false);
  
  // Error state
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Get user ID on component mount
  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user && user.id) {
      setUserId(user.id);
    }
  }, []);
  
  // Generate Q&A pairs from document text or knowledge base
  const handleGenerateQAPairs = async () => {
    // Validate input based on content source
    if (contentSource === 'provided_text' && !documentText.trim()) {
      setError('Please provide document text or switch to knowledge base mode.');
      return;
    }
    
    // Note: Knowledge base query is optional - if not provided, it will use general topics
    
    setIsGenerating(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const result = await trainerService.generateQAPairs(
        documentText, 
        numQuestions, 
        contentSource, 
        knowledgeBaseQuery
      );
      
      // Format Q&A pairs with IDs
      const formattedPairs = result.qa_pairs.map((pair, index) => ({
        id: `new-${index}`,
        question: pair.question,
        answer: pair.answer,
        category: pair.category || 'general',
        isNew: true,
        isEditing: false
      }));
      
      setQaPairs(formattedPairs);
      
      // Show success message with source info
      let sourceInfo;
      if (contentSource === 'knowledge_base') {
        sourceInfo = knowledgeBaseQuery.trim() 
          ? `from knowledge base (query: "${knowledgeBaseQuery}")`
          : 'from knowledge base (general topics)';
      } else {
        sourceInfo = 'from provided text';
      }
      setSuccessMessage(`Successfully generated ${formattedPairs.length} Q&A pairs ${sourceInfo}.`);
      
    } catch (error) {
      setError(`Error generating Q&A pairs: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Toggle selection of a Q&A pair
  const toggleSelection = (index) => {
    const pairId = qaPairs[index].id;
    
    if (selectedQaPairs.includes(pairId)) {
      setSelectedQaPairs(selectedQaPairs.filter(id => id !== pairId));
    } else {
      setSelectedQaPairs([...selectedQaPairs, pairId]);
    }
  };
  
  // Toggle editing mode for a Q&A pair
  const toggleEditing = (index) => {
    const updatedPairs = [...qaPairs];
    updatedPairs[index].isEditing = !updatedPairs[index].isEditing;
    setQaPairs(updatedPairs);
  };
  
  // Update a Q&A pair
  const updateQAPair = (index, field, value) => {
    const updatedPairs = [...qaPairs];
    updatedPairs[index][field] = value;
    setQaPairs(updatedPairs);
  };
  
  // Validate a single Q&A pair
  const validateQAPair = async (index) => {
    const pair = qaPairs[index];
    
    setIsValidating(true);
    setError(null);
    
    try {
      const result = await trainerService.validateAnswer(pair.question, pair.answer);
      
      const updatedResults = { ...validationResults };
      updatedResults[index] = result;
      setValidationResults(updatedResults);
    } catch (error) {
      setError(`Error validating Q&A pair: ${error.message}`);
    } finally {
      setIsValidating(false);
    }
  };
  
  // Save selected Q&A pairs
  const saveSelectedQAPairs = async () => {
    if (selectedQaPairs.length === 0) {
      setError('Please select at least one Q&A pair to save.');
      return;
    }
    
    setIsGenerating(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const pairsToSave = selectedQaPairs.map(id => {
        const pair = qaPairs.find(p => p.id === id);
        return {
          question: pair.question,
          answer: pair.answer
        };
      });
      
      await trainerService.createOrUpdateQAPair(pairsToSave, userId);
      setSuccessMessage(`Successfully saved ${pairsToSave.length} Q&A pairs.`);
      
      // Mark saved pairs as not new
      const updatedPairs = qaPairs.map(pair => {
        if (selectedQaPairs.includes(pair.id)) {
          return { ...pair, isNew: false };
        }
        return pair;
      });
      
      setQaPairs(updatedPairs);
      setSelectedQaPairs([]);
    } catch (error) {
      setError(`Error saving Q&A pairs: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Fine-tune the model with selected Q&A pairs
  const fineTuneModel = async () => {
    if (selectedQaPairs.length === 0) {
      setError('Please select at least one Q&A pair for fine-tuning.');
      return;
    }
    
    setIsGenerating(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      const pairsToTrain = selectedQaPairs.map(id => {
        const pair = qaPairs.find(p => p.id === id);
        return {
          question: pair.question,
          answer: pair.answer
        };
      });
      
      const result = await trainerService.fineTune(pairsToTrain);
      setSuccessMessage(`Fine-tuning job started. Job ID: ${result.job_id}`);
    } catch (error) {
      setError(`Error starting fine-tuning: ${error.message}`);
    } finally {
      setIsGenerating(false);
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="mb-6 text-2xl font-bold text-white">Chatbot Trainer</h1>
      
      {/* Document Input Section */}
      <div className="mb-8 rounded-lg bg-gray-800 p-6 shadow-lg border border-gray-700">
        <h2 className="mb-4 text-xl font-semibold text-white">📝 Generate Q&A Pairs</h2>
        
        {/* Content Source Selection */}
        <div className="mb-6">
          <label className="mb-3 block font-medium text-gray-300">
            Content Source
          </label>
          <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="contentSource"
                value="provided_text"
                checked={contentSource === 'provided_text'}
                onChange={(e) => setContentSource(e.target.value)}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 bg-gray-700"
              />
              <span className="text-gray-300">📝 Use Provided Text</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="contentSource"
                value="knowledge_base"
                checked={contentSource === 'knowledge_base'}
                onChange={(e) => setContentSource(e.target.value)}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 bg-gray-700"
              />
              <span className="text-gray-300">🧠 Use Knowledge Base</span>
            </label>
          </div>
        </div>
        
        {/* Conditional Input Fields */}
        {contentSource === 'provided_text' ? (
          <div className="mb-4">
            <label htmlFor="documentText" className="mb-2 block font-medium text-gray-300">
              Document Text
            </label>
            <textarea
              id="documentText"
              className="w-full rounded-md border border-gray-600 bg-gray-700 p-2 text-white focus:border-blue-500 focus:ring-blue-500"
              rows="6"
              value={documentText}
              onChange={(e) => setDocumentText(e.target.value)}
              placeholder="Paste document text here to generate Q&A pairs..."
            ></textarea>
          </div>
        ) : (
          <div className="mb-4">
            <label htmlFor="knowledgeBaseQuery" className="mb-2 block font-medium text-gray-300">
              Knowledge Base Search Query <span className="text-sm text-gray-400">(Optional)</span>
            </label>
            <input
              type="text"
              id="knowledgeBaseQuery"
              className="w-full rounded-md border border-gray-600 bg-gray-700 p-2 text-white focus:border-blue-500 focus:ring-blue-500"
              value={knowledgeBaseQuery}
              onChange={(e) => setKnowledgeBaseQuery(e.target.value)}
              placeholder="Enter search terms to find specific content (e.g., 'water softener installation') or leave empty for general topics..."
            />
            <p className="mt-2 text-sm text-gray-400">
              💡 If you provide a search query, the system will find relevant content. If left empty, it will generate Q&A pairs from general topics in the knowledge base.
            </p>
          </div>
        )}
        
        <div className="mb-4">
          <label htmlFor="numQuestions" className="mb-2 block font-medium text-gray-300">
            Number of Questions
          </label>
          <input
            type="number"
            id="numQuestions"
            className="w-full max-w-xs rounded-md border border-gray-600 bg-gray-700 p-2 text-white focus:border-blue-500 focus:ring-blue-500"
            min="1"
            max="20"
            value={numQuestions}
            onChange={(e) => setNumQuestions(parseInt(e.target.value))}
          />
        </div>
        
        <button
          onClick={handleGenerateQAPairs}
          disabled={isGenerating}
          className="rounded-md bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:bg-blue-400"
        >
          {isGenerating ? 'Generating...' : 'Generate Q&A Pairs'}
        </button>
      </div>
      
      {/* Error and Success Messages */}
      {error && (
        <div className="mb-4 rounded-md bg-red-900/30 p-3 text-red-400 border border-red-800/50">
          <p>{error}</p>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-4 rounded-md bg-green-900/30 p-3 text-green-300 border border-green-800/50">
          <p>{successMessage}</p>
        </div>
      )}
      
      {/* Q&A Pairs Section */}
      {qaPairs.length > 0 && (
        <div className="mb-8 rounded-lg bg-gray-800 p-6 shadow-lg border border-gray-700">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-white">Generated Q&A Pairs</h2>
            <div className="flex space-x-2">
              <button
                onClick={saveSelectedQAPairs}
                disabled={isGenerating || selectedQaPairs.length === 0}
                className="rounded-md bg-green-600 px-4 py-2 text-white hover:bg-green-700 disabled:bg-green-400"
              >
                Save Selected
              </button>
              <button
                onClick={fineTuneModel}
                disabled={isGenerating || selectedQaPairs.length === 0}
                className="rounded-md bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:bg-purple-400"
              >
                Fine-tune Model
              </button>
            </div>
          </div>
          
          <div className="space-y-4">
            {qaPairs.map((pair, index) => (
              <div
                key={pair.id}
                className={`rounded-lg border p-4 ${
                  selectedQaPairs.includes(pair.id)
                    ? 'border-blue-500 bg-blue-900/20'
                    : 'border-gray-600 bg-gray-700'
                } ${pair.isNew ? 'border-l-4 border-l-green-500' : ''}`}
              >
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={`select-${index}`}
                      checked={selectedQaPairs.includes(pair.id)}
                      onChange={() => toggleSelection(index)}
                      className="mr-2 h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor={`select-${index}`} className="font-medium text-white">
                      Q&A Pair #{index + 1}
                    </label>
                    {pair.isNew && (
                      <span className="ml-2 rounded-full bg-green-900/30 px-2 py-0.5 text-xs text-green-300">
                        New
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => validateQAPair(index)}
                      disabled={isValidating}
                      className="rounded bg-gray-600 px-2 py-1 text-sm text-white hover:bg-gray-500"
                    >
                      Validate
                    </button>
                    <button
                      onClick={() => toggleEditing(index)}
                      className="rounded bg-gray-600 px-2 py-1 text-sm text-white hover:bg-gray-500"
                    >
                      {pair.isEditing ? 'Done' : 'Edit'}
                    </button>
                  </div>
                </div>
                
                {/* Question */}
                <div className="mb-3">
                  <div className="mb-1 text-sm font-medium text-gray-300">Question:</div>
                  {pair.isEditing ? (
                    <textarea
                      value={pair.question}
                      onChange={(e) => updateQAPair(index, 'question', e.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-gray-700 p-2 text-white focus:border-blue-500 focus:ring-blue-500"
                      rows="2"
                    ></textarea>
                  ) : (
                    <div className="rounded-md bg-gray-800 p-2 text-white">{pair.question}</div>
                  )}
                </div>
                
                {/* Answer */}
                <div className="mb-3">
                  <div className="mb-1 text-sm font-medium text-gray-300">Answer:</div>
                  {pair.isEditing ? (
                    <textarea
                      value={pair.answer}
                      onChange={(e) => updateQAPair(index, 'answer', e.target.value)}
                      className="w-full rounded-md border border-gray-600 bg-gray-700 p-2 text-white focus:border-blue-500 focus:ring-blue-500"
                      rows="4"
                    ></textarea>
                  ) : (
                    <div className="rounded-md bg-gray-800 p-2 text-white whitespace-pre-wrap">{pair.answer}</div>
                  )}
                </div>
                
                {/* Validation Results */}
                {validationResults[index] && (
                  <div className="mt-2 rounded-md bg-gray-800 p-3 text-sm">
                    <div className="mb-1 font-medium text-gray-300">Validation Results:</div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-gray-400">Accuracy: </span>
                        <span className={`font-medium ${getScoreColor(validationResults[index].accuracy_score)}`}>
                          {Math.round(validationResults[index].accuracy_score * 100)}%
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400">Relevance: </span>
                        <span className={`font-medium ${getScoreColor(validationResults[index].relevance_score)}`}>
                          {Math.round(validationResults[index].relevance_score * 100)}%
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400">Completeness: </span>
                        <span className={`font-medium ${getScoreColor(validationResults[index].completeness_score)}`}>
                          {Math.round(validationResults[index].completeness_score * 100)}%
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400">Groundedness: </span>
                        {validationResults[index].context_found ? (
                          <span className={`font-medium ${getScoreColor(validationResults[index].groundedness_score)}`}>
                            {Math.round(validationResults[index].groundedness_score * 100)}%
                          </span>
                        ) : (
                          <span className="font-medium text-gray-500" title="No matching knowledge-base context found">
                            N/A
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="text-gray-400">Overall: </span>
                        <span className={`font-medium ${getScoreColor(validationResults[index].overall_score)}`}>
                          {Math.round(validationResults[index].overall_score * 100)}%
                        </span>
                      </div>
                    </div>
                    {(validationResults[index].reason || validationResults[index].feedback) && (
                      <div className="mt-2">
                        <span className="text-gray-400">Feedback: </span>
                        <span className="text-white">{validationResults[index].reason || validationResults[index].feedback}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get color based on score
const getScoreColor = (score) => {
  if (score >= 0.8) return 'text-green-400';
  if (score >= 0.6) return 'text-yellow-400';
  return 'text-red-400';
};

export default TrainerPage;
